<template>
  <ai-booking-bar
    ref="bookingBar"
    v-model:rooms-and-guests="roomsAndGuests"
    v-model:dates="dates"
    :mobile-items="mobileItems"
    :minimum-stay-duration="minimumStayDuration"
    :children-max-age="childrenMaxAge"
    mobile-show-room-count-on-dates>
    <template #input-before>
      <div class="BookingBarContext-hotelName">
        <ai-icon
          name="location"
          class="BookingBarContext-hotelName-icon"
          :description="$t('booking.recap.icons.location')" />
        <ai-typo as="span" variant="paragraph-04-bold">
          {{ hotelName }}
        </ai-typo>
      </div>

      <ai-separator margin="none" vertical />
    </template>

    <!-- Waiting for ACCORDAI-166 -->
    <!-- <template #input-after>
      <ai-button
        variant="tertiary"
        icon-left="plus"
        :label="$t('booking.context.specialRates')" />
    </template> -->

    <template #action>
      <ai-button
        :label="$t('booking.context.apply')"
        :loading="loading"
        @click="submit" />
    </template>

    <template #mobile-submit>
      <ai-button
        :label="$t('booking.context.apply')"
        :loading="loading"
        @click="submit" />
    </template>

    <template v-if="empty" #errors>
      <div class="BookingBarContext-error">
        <ai-icon name="warning" class="BookingBarContext-errorIcon" />
        <div class="BookingBarContext-errorText">
          <ai-typo as="span" variant="paragraph-04-regular">
            {{ $t('booking.bar.errors.empty') }}
          </ai-typo>
          <ai-link v-if="!isMobileFormOpen" @click="resetBookingBarContext">
            {{ $t('booking.context.reset') }}
          </ai-link>
        </div>
      </div>
    </template>
  </ai-booking-bar>
</template>

<script setup lang="ts">
import { differenceInCalendarDays } from 'date-fns';

import { formatDate } from '~/helpers/date.helper';

import type { UnconfiguredGuests } from '../../interfaces';
import { useBookingStore } from '../../store';

import type { MobileItem } from '~~/domains/ux/organisms/AiBookingBar/AiBookingBar.vue';
import AiBookingBar from '~~/domains/ux/organisms/AiBookingBar/AiBookingBar.vue';
import { extractRoomsAndGuestsFromStore } from '~~/helpers/booking.helper';

type Props = {
  hotelName?: string;
  minimumStayDuration?: number;
  loading?: boolean;
  empty?: boolean;
  childrenMaxAge?: number;
};

type Emits = {
  (
    event: 'submit',
    payload: {
      roomsAndGuests: Array<UnconfiguredGuests>;
      dateIn: string;
      nights: number;
    },
  ): void;
  (event: 'reset'): void;
};

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const bookingStore = useBookingStore();

const bookingBar = ref<typeof AiBookingBar>();

const openMobileForm = computed(() => bookingBar.value?.openMobileForm);
const closeMobileForm = computed(() => bookingBar.value?.closeMobileForm);
const isMobileFormOpen = computed(() => bookingBar.value?.isMobileFormOpen);

const initialDates = () => {
  if (
    !bookingStore.isoDateIn ||
    !bookingStore.isoDateOut ||
    !bookingStore.nights
  ) {
    return [];
  }

  const firstDate = new Date(bookingStore.isoDateIn);
  const secondDate = new Date(bookingStore.isoDateOut);

  return [firstDate, secondDate];
};

const roomsAndGuests = ref(extractRoomsAndGuestsFromStore(bookingStore));
const dates = ref(initialDates());

const mobileItems = computed<MobileItem[]>(() => [
  {
    bold: true,
    icon: 'location',
    key: 'hotel-name',
    label: props.hotelName as string,
    position: 'before',
  },
]);

const submit = () => {
  const [firstDate, lastDate] = dates.value;

  if (
    roomsAndGuests.value.some(room => room.childrenAges.some(age => age < 0))
  ) {
    return;
  }

  const dateIn = formatDate(firstDate, 'yyyy-MM-dd');
  const nights = Math.abs(differenceInCalendarDays(firstDate, lastDate));

  const payload = {
    dateIn,
    nights,
    roomsAndGuests: roomsAndGuests.value,
  };

  emits('submit', payload);
};

const resetBookingBarContext = () => {
  dates.value = initialDates();
  roomsAndGuests.value = extractRoomsAndGuestsFromStore(bookingStore);
  emits('reset');
};

defineExpose({
  closeMobileForm,
  openMobileForm,
  isMobileFormOpen,
  resetBookingBarContext,
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';

.BookingBarContext-hotelName {
  display: flex;
  align-items: center;
}

.BookingBarContext-hotelName-icon {
  color: colors.$gold-900;
  margin-right: constants.$margin-00;
}

.BookingBarContext-error {
  flex-basis: 40%;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 constants.$margin-01;
}

.BookingBarContext-errorIcon {
  color: colors.$red-700;
  fill: colors.$red-700;
  margin-right: constants.$inner-03;
}
</style>
